import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { pick as _pick } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { GetOrganizationDetailsGQL, TagType, UpdateAddressTagGQL, UpdateTagGQL } from 'generated/graphql';

import {
  FieldResponseFormModel
} from 'app/field-shared/forms/field-response/field-response-form.component';
import { TagCreationService } from 'app/field-shared/tag-creation.service';
import { ToastrService } from 'core/toastr.service';
import { isNilOrEmpty } from 'helpers';
import { AddressFormModel } from 'shared/forms/address-form/address-form.component';
import { selectCurrentCampaign } from 'app/_state/campaign-context/campaign-context-selectors';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';

export interface AddOrUpdateFieldResponseDialogData {
  tagCategoryId: string;
  tagType: TagType;
  campaignId?: string;
  tag?: {
    id?: string;
    name: string;
    address?: AddressFormModel;
  };
  isUniversal: boolean;
}

@Component({
  selector: 'app-add-or-update-field-response-dialog',
  templateUrl: `./add-or-update-field-response.dialog.html`,
  styleUrls: [`./add-or-update-field-response.dialog.scss`]
})
export class AddOrUpdateFieldResponseDialog extends WithDisposableSubject() implements OnInit {
  isSaving = false;
  isUniversal = false;

  formGroup: UntypedFormGroup;

  get isCreate() {
    return isNilOrEmpty(this.data?.tag?.id);
  }

  get isAddress() {
    return this.data.tagType === TagType.Address;
  }

  entities: any[];

  static open(dialog: MatDialog, config: MatDialogConfig<AddOrUpdateFieldResponseDialogData>) {
    return dialog.open<AddOrUpdateFieldResponseDialog,
      AddOrUpdateFieldResponseDialogData,
      { id: string; name: string; tagType: TagType }>(AddOrUpdateFieldResponseDialog, config);
  }

  constructor(
    private _tagCreationService: TagCreationService,
    private _updateTagGQL: UpdateTagGQL,
    private _getOrganizationDetailsGQL: GetOrganizationDetailsGQL,
    private _updateAddressTagGQL: UpdateAddressTagGQL,
    private _cd: ChangeDetectorRef,
    private _toastrService: ToastrService,
    private _store: Store,
    public dialogRef: MatDialogRef<AddOrUpdateFieldResponseDialog>,
    @Inject(MAT_DIALOG_DATA) public data: AddOrUpdateFieldResponseDialogData) {
      super();
  }

  ngOnInit() {
    this._store.select(selectCurrentCampaign).pipe(
      takeUntil(this.disposer),
      switchMap(campaign => {
        if (campaign?.defaultCountry) {
          return of(campaign.defaultCountry);
        }
        return this._getOrganizationDetailsGQL.fetch().pipe(
          map(res => res.data.getOrganization.defaultCountry)
        );
      })
    ).subscribe(defaultCountry => {
      this.isUniversal = this.data.isUniversal;

      const formControl = new UntypedFormControl({
        name: this.data?.tag?.name,
        address: this.data?.tag?.address || {
          country: defaultCountry || 'US',
        }
      });

      this.formGroup = new UntypedFormGroup({
        fieldResponse: formControl
      });

      this._cd.markForCheck();
    });
  }


  onSubmit() {
    this.formGroup.updateValueAndValidity();
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid) {
      return;
    }

    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    if (this.isCreate) {
      this._createTag();
    } else {
      this._updateTag();
    }
  }

  _createTag() {
    const formValue: FieldResponseFormModel = this.formGroup.get('fieldResponse').value;

    this._tagCreationService.addNewTag(
      this.data.tagType,
      this.data.tagCategoryId,
      formValue.name,
      this.data.campaignId,
      formValue.address
    ).pipe(
      finalize(() => this.isSaving = false)
    )
      .subscribe(r => {
        this._toastrService.success('Response Added');
        this.close(r.data.createTag.tag);
        this._cd.markForCheck();
      });
  }

  _updateTag() {
    const formValue: FieldResponseFormModel = this.formGroup.get('fieldResponse').value;

    let obs: Observable<{ id: string; name: string }> = null;
    if (this.data.tagType === TagType.Address) {
      const address = _pick(formValue.address, [
        'streetAddress', 'city', 'state', 'zip', 'country'
      ]);
      obs = this._updateAddressTagGQL.mutate({
        input: {
          tagId: this.data.tag.id,
          name: formValue.name,
          address
        }
      }).pipe(
        map(res => res.data.updateAddressTag.tag)
      );

    } else {
      obs = this._updateTagGQL.mutate({
        input: {
          tagId: this.data.tag.id,
          tagType: this.data.tagType,
          name: formValue.name
        }
      })
        .pipe(
          map(res => res.data.updateTag.tag)
        );;
    }

    obs.pipe(
      finalize(() => this.isSaving = false)
    )
      .subscribe(
        (d) => {
          this._toastrService.success('Changes Saved');
          this.close(d);
        });

  }

  // Close dialog and pass along event data.
  close(event: any = null) {
    this.dialogRef.close(event);
  }
}
